import { useEffect, useRef, useState } from 'react';
import { NarrationRecorder, downloadBlob } from '../runtime/recorder';

interface Props {
  deckId: string;
  onCancel: () => void;
}

export function RecordingHUD({ deckId, onCancel }: Props) {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'stopped'>('idle');
  const [level, setLevel] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<NarrationRecorder | null>(null);
  const [lastResult, setLastResult] = useState<{
    blob: Blob;
    mimeType: string;
    durationMs: number;
    sampleRate: number;
  } | null>(null);

  useEffect(() => {
    return () => recorderRef.current?.cancel();
  }, []);

  const start = async () => {
    setError(null);
    const r = new NarrationRecorder({
      onLevel: setLevel,
      onTimeUpdate: setElapsedMs,
      onError: (e) => setError(e.message),
    });
    recorderRef.current = r;
    try {
      await r.start();
      setPhase('recording');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const stop = async () => {
    const r = recorderRef.current;
    if (!r) return;
    const out = await r.stop();
    recorderRef.current = null;
    if (out) {
      setLastResult(out);
      setPhase('stopped');
    }
  };

  const downloadNarration = () => {
    if (!lastResult) return;
    const ext = lastResult.mimeType.includes('mp4') ? 'm4a' : 'webm';
    downloadBlob(lastResult.blob, `${deckId}.narration.${ext}`);
  };

  const peak = Math.max(0, Math.min(1, level));
  const elapsedS = (elapsedMs / 1000).toFixed(1);

  return (
    <div style={hudStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={dotStyle(phase === 'recording')} />
        <div style={{ fontSize: 14, color: '#9CA3AF', fontFamily: 'ui-monospace, monospace' }}>
          REC
        </div>
        <div style={{ fontSize: 18, color: '#fff', fontFamily: 'ui-monospace, monospace' }}>
          {elapsedS}s
        </div>
        <div style={meterContainerStyle}>
          <div style={{ ...meterFillStyle, width: `${peak * 100}%`, background: peak > 0.85 ? '#DC2626' : peak > 0.5 ? '#F5B800' : '#17A34A' }} />
        </div>
      </div>

      {error && (
        <div style={{ color: '#FCA5A5', fontSize: 14, marginTop: 8 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        {phase === 'idle' && (
          <>
            <button style={btnPrimary} onClick={start}>● Start recording</button>
            <button style={btnGhost} onClick={onCancel}>Cancel</button>
          </>
        )}
        {phase === 'recording' && (
          <>
            <button style={btnDanger} onClick={stop}>■ Stop</button>
          </>
        )}
        {phase === 'stopped' && lastResult && (
          <>
            <button style={btnPrimary} onClick={downloadNarration}>⬇ Download narration</button>
            <button style={btnGhost} onClick={() => { setPhase('idle'); setLastResult(null); setElapsedMs(0); }}>Re-record</button>
            <button style={btnGhost} onClick={onCancel}>Done</button>
          </>
        )}
      </div>

      {lastResult && phase === 'stopped' && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#9CA3AF' }}>
          Captured {(lastResult.blob.size / 1024).toFixed(1)} KB ·{' '}
          {(lastResult.durationMs / 1000).toFixed(1)}s · {lastResult.sampleRate} Hz · {lastResult.mimeType}
        </div>
      )}
    </div>
  );
}

const hudStyle: React.CSSProperties = {
  position: 'fixed',
  top: 20,
  right: 20,
  background: 'rgba(15, 25, 35, 0.92)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
  padding: '16px 20px',
  minWidth: 320,
  fontFamily: 'Inter, sans-serif',
  zIndex: 50,
  backdropFilter: 'blur(8px)',
};

const dotStyle = (active: boolean): React.CSSProperties => ({
  width: 14,
  height: 14,
  borderRadius: 7,
  background: active ? '#DC2626' : '#5C6B73',
  boxShadow: active ? '0 0 12px #DC2626' : undefined,
  animation: active ? 'rec-pulse 1.4s ease-in-out infinite' : undefined,
});

const meterContainerStyle: React.CSSProperties = {
  flex: 1,
  height: 8,
  background: 'rgba(255,255,255,0.06)',
  borderRadius: 4,
  overflow: 'hidden',
  minWidth: 120,
};

const meterFillStyle: React.CSSProperties = {
  height: '100%',
  transition: 'width 80ms linear, background 200ms',
};

const btnBase: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnPrimary: React.CSSProperties = {
  ...btnBase,
  background: '#F5B800',
  color: '#0F1923',
};

const btnDanger: React.CSSProperties = {
  ...btnBase,
  background: '#DC2626',
  color: '#fff',
};

const btnGhost: React.CSSProperties = {
  ...btnBase,
  background: 'transparent',
  color: '#E8E8E8',
  border: '1px solid rgba(255,255,255,0.14)',
};
